import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Repeat, Pencil, Ban, Check, X } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { API } from "../../../App";
import { VatPreview, VatRateSelect } from "./VatRateSelect";
import { money, presetRate, rateError } from "./constants";

const FREQUENCIES = ["monthly", "quarterly", "annual"];
const blankForm = (today) => ({
  category_id: "", payee: "", description: "", frequency: "monthly",
  amount: "", vat_treatment: "standard_23", vat_rate_percent: 23,
  start_date: today, end_date: "",
});

const TemplateFields = ({ f, set, prefix, categories, bounds }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
    <div>
      <Label className="text-xs text-slate-400">Category</Label>
      <Select value={f.category_id} onValueChange={(v) => {
        const cat = categories.find((c) => c.category_id === v);
        set({ ...f, category_id: v,
              vat_treatment: cat?.default_vat_treatment || f.vat_treatment,
              vat_rate_percent: cat?.default_vat_rate_percent ?? f.vat_rate_percent });
      }}>
        <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-9"
                       data-testid={`${prefix}-category-select`}>
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          {categories.map((c) => (
            <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs text-slate-400">Payee</Label>
      <Input value={f.payee} onChange={(e) => set({ ...f, payee: e.target.value })}
             className="bg-slate-800 border-slate-700 text-white h-9"
             data-testid={`${prefix}-payee-input`} />
    </div>
    <div>
      <Label className="text-xs text-slate-400">Amount incl. VAT (blank = varies)</Label>
      <Input type="number" step="0.01" value={f.amount}
             onChange={(e) => set({ ...f, amount: e.target.value })}
             className="bg-slate-800 border-slate-700 text-white h-9"
             data-testid={`${prefix}-amount-input`} />
      <VatPreview gross={f.amount} treatment={f.vat_treatment}
                  ratePercent={f.vat_rate_percent} prefix={prefix} />
    </div>
    <div>
      <Label className="text-xs text-slate-400">How often</Label>
      <Select value={f.frequency} onValueChange={(v) => set({ ...f, frequency: v })}>
        <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-9"
                       data-testid={`${prefix}-frequency-select`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FREQUENCIES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs text-slate-400">VAT treatment</Label>
      <VatRateSelect treatment={f.vat_treatment} ratePercent={f.vat_rate_percent}
                     onChange={(p) => set({ ...f, ...p })} prefix={prefix} />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs text-slate-400">First payment</Label>
        <Input type="date" value={f.start_date} min={bounds?.min_date} max={bounds?.max_date}
               onChange={(e) => set({ ...f, start_date: e.target.value })}
               className="bg-slate-800 border-slate-700 text-white h-9"
               data-testid={`${prefix}-start-input`} />
      </div>
      <div>
        <Label className="text-xs text-slate-400">Ends (optional)</Label>
        <Input type="date" value={f.end_date || ""} min={f.start_date} max={bounds?.max_date}
               onChange={(e) => set({ ...f, end_date: e.target.value })}
               className="bg-slate-800 border-slate-700 text-white h-9"
               data-testid={`${prefix}-end-input`} />
      </div>
    </div>
  </div>
);

export const RecurringTab = ({ categories, bounds, onChanged }) => {
  const today = bounds?.today || new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(blankForm(today));
  const [templates, setTemplates] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [stopping, setStopping] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/accounting/recurring`);
    setTemplates(r.data.templates || []);
  };
  React.useEffect(() => { load(); }, []);

  const payload = (f) => ({
    category_id: f.category_id, payee: f.payee, description: f.description,
    frequency: f.frequency, start_date: f.start_date,
    end_date: f.end_date || null, vat_treatment: f.vat_treatment,
    vat_rate_percent: f.vat_rate_percent === "" ? null : Number(f.vat_rate_percent),
    amount: f.amount === "" ? null : Number(f.amount),
  });

  const runPreview = async (f, templateId) => {
    if (!f.category_id || !f.start_date) {
      toast.error("Pick a category and a start date");
      return;
    }
    const err = rateError(f.vat_treatment, f.vat_rate_percent);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const url = `${API}/accounting/recurring/preview${templateId ? `?template_id=${templateId}` : ""}`;
      const r = await axios.post(url, payload(f));
      setPreview({ ...r.data, form: f, templateId });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build the preview");
    } finally { setBusy(false); }
  };

  const confirmCreate = async () => {
    setBusy(true);
    try {
      const created = await axios.post(`${API}/accounting/recurring`, payload(preview.form));
      const tid = created.data.template_id;
      const posted = await axios.post(
        `${API}/accounting/recurring/post-due?back_post=true&template_id=${tid}`);
      toast.success(`Template saved · ${posted.data.created} entr(ies) posted`);
      setPreview(null);
      setForm(blankForm(today));
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the template");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const body = { ...payload(editForm), apply_to: editForm.apply_to || "future_only" };
      if (body.apply_to === "posted_from") body.apply_from = editForm.apply_from;
      const r = await axios.patch(`${API}/accounting/recurring/${editing}`, body);
      toast.success(r.data.entries_updated
        ? `Saved · ${r.data.entries_updated} posted entr(ies) updated`
        : "Saved · already-posted entries left as they are");
      setEditing(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update the template");
    } finally { setBusy(false); }
  };

  const stopTemplate = async (voidFrom) => {
    setBusy(true);
    try {
      const qs = voidFrom ? `?void_posted_from=${voidFrom}` : "";
      const r = await axios.post(
        `${API}/accounting/recurring/${stopping.template_id}/deactivate${qs}`);
      toast.success(`Stopped · ${r.data.entries_voided} entr(ies) voided, `
        + `${r.data.posted_entries - r.data.entries_voided} kept`);
      setStopping(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not stop the template");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="finance-recurring">
      <div className="rounded border border-slate-800 bg-slate-800/30 p-3 space-y-3">
        <p className="text-xs text-slate-400">
          A backdated first payment is fine — you will see every entry it creates before anything is saved.
        </p>
        <TemplateFields f={form} set={setForm} prefix="template"
                        categories={categories} bounds={bounds} />
        <Button size="sm" disabled={busy} onClick={() => runPreview(form)}
                className="bg-saturn-orange hover:bg-saturn-orange/80 text-black"
                data-testid="template-preview-btn">
          <Repeat className="w-3 h-3 mr-1" /> Preview entries
        </Button>
      </div>

      <div className="space-y-1">
        {(templates || []).map((t) => (
          <div key={t.template_id} className="border-b border-slate-800 py-2"
               data-testid={`template-row-${t.template_id}`}>
            <div className="flex flex-wrap justify-between items-center gap-2 text-xs">
              <span className={t.active ? "text-slate-300" : "text-slate-600 line-through"}>
                {t.payee || "—"} · {t.frequency} · from {t.start_date}
                {t.end_date ? ` to ${t.end_date}` : ""} · {t.vat_treatment}
                {t.vat_rate_percent != null && ` (${Number(t.vat_rate_percent)}%)`}
              </span>
              <span className="flex items-center gap-3">
                <span className="font-mono text-white">
                  {t.amount == null ? <span className="text-amber-300">varies</span> : money(t.amount)}
                </span>
                <button className="text-slate-400 hover:text-white" title="Edit template"
                        onClick={() => {
                          setEditing(t.template_id);
                          setEditForm({
                            category_id: t.category_id, payee: t.payee || "",
                            description: t.description || "", frequency: t.frequency,
                            amount: t.amount == null ? "" : String(t.amount),
                            vat_treatment: t.vat_treatment,
                            vat_rate_percent: t.vat_rate_percent
                              ?? presetRate(t.vat_treatment) ?? 0,
                            start_date: t.start_date,
                            end_date: t.end_date || "", apply_to: "future_only",
                            apply_from: t.start_date,
                          });
                        }}
                        data-testid={`template-edit-btn-${t.template_id}`}>
                  <Pencil className="w-3 h-3" />
                </button>
                {t.active && (
                  <button className="text-red-400 hover:text-red-300" title="Stop template"
                          onClick={() => setStopping(t)}
                          data-testid={`template-stop-btn-${t.template_id}`}>
                    <Ban className="w-3 h-3" />
                  </button>
                )}
              </span>
            </div>
            {editing === t.template_id && (
              <div className="mt-3 rounded border border-slate-700 bg-slate-900 p-3 space-y-3"
                   data-testid={`template-edit-row-${t.template_id}`}>
                <TemplateFields f={editForm} set={setEditForm} prefix="template-edit"
                                categories={categories} bounds={bounds} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-400">Apply changes to</Label>
                    <Select value={editForm.apply_to}
                            onValueChange={(v) => setEditForm({ ...editForm, apply_to: v })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-9"
                                     data-testid="template-apply-to-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="future_only">Future entries only</SelectItem>
                        <SelectItem value="posted_from">Also update already-posted entries</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editForm.apply_to === "posted_from" && (
                    <div>
                      <Label className="text-xs text-slate-400">…posted on or after</Label>
                      <Input type="date" value={editForm.apply_from || ""}
                             min={bounds?.min_date} max={bounds?.max_date}
                             onChange={(e) => setEditForm({ ...editForm, apply_from: e.target.value })}
                             className="bg-slate-800 border-slate-700 text-white h-9"
                             data-testid="template-apply-from-input" />
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled={busy} onClick={saveEdit}
                          className="bg-saturn-orange hover:bg-saturn-orange/80 text-black"
                          data-testid="template-edit-save-btn">
                    <Check className="w-3 h-3 mr-1" /> Save changes
                  </Button>
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-200"
                          onClick={() => runPreview(editForm, t.template_id)}
                          data-testid="template-edit-preview-btn">
                    <Repeat className="w-3 h-3 mr-1" /> Preview entries
                  </Button>
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-200"
                          onClick={() => setEditing(null)}
                          data-testid="template-edit-cancel-btn">
                    <X className="w-3 h-3 mr-1" /> Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {templates && !templates.length && (
          <p className="text-xs text-slate-500">No recurring overheads set up yet.</p>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {preview?.count_to_create || 0} entr{preview?.count_to_create === 1 ? "y" : "ies"} will be created
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3" data-testid="template-preview">
              <p className="text-xs text-slate-400" data-testid="template-preview-summary">
                {preview.category_name} · {preview.count} due up to {preview.until}
                {preview.already_posted ? ` · ${preview.already_posted} already posted` : ""}
                {preview.drafts ? ` · ${preview.drafts} awaiting an amount` : ""}
                {preview.next_due_after_today
                  ? ` · next after today: ${preview.next_due_after_today}`
                  : ""}
              </p>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr><th className="text-left py-1">Date</th><th className="text-right">Net</th>
                      <th className="text-right">VAT</th><th className="text-right">Rate</th>
                      <th className="text-right">Gross</th>
                      <th className="text-right">Status</th></tr>
                  </thead>
                  <tbody>
                    {preview.entries.map((e) => (
                      <tr key={e.expense_date} className="border-t border-slate-800"
                          data-testid={`template-preview-row-${e.expense_date}`}>
                        <td className="py-1 text-slate-300">{e.expense_date}</td>
                        <td className="text-right font-mono text-slate-300">{money(e.net_amount)}</td>
                        <td className="text-right font-mono text-slate-400">{money(e.vat_amount)}</td>
                        <td className="text-right font-mono text-slate-500">
                          {Number(e.vat_rate_percent ?? 0)}%
                        </td>
                        <td className="text-right font-mono text-white">
                          {e.amount_gross == null
                            ? <span className="text-amber-300">needs amount</span>
                            : money(e.amount_gross)}
                        </td>
                        <td className="text-right text-slate-500">
                          {e.already_posted ? "already posted" : "new"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-700 text-white font-semibold">
                      <td className="py-2">Total to create</td>
                      <td className="text-right font-mono">{money(preview.total_net)}</td>
                      <td className="text-right font-mono">{money(preview.total_vat)}</td>
                      <td />
                      <td className="text-right font-mono" data-testid="template-preview-total">
                        {money(preview.total_gross)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-200"
                    onClick={() => setPreview(null)} data-testid="template-preview-cancel">
              Cancel
            </Button>
            {!preview?.templateId && (
              <Button size="sm" disabled={busy} onClick={confirmCreate}
                      className="bg-saturn-orange hover:bg-saturn-orange/80 text-black"
                      data-testid="template-preview-confirm">
                <Check className="w-3 h-3 mr-1" /> Confirm and post
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stopping} onOpenChange={(o) => !o && setStopping(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Stop this recurring overhead?</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400">
            Nothing more will be posted for {stopping?.payee || "this template"}. Entries
            already posted stay in the ledger unless you void them — voiding keeps the row
            but takes it out of every total.
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-200"
                    disabled={busy} onClick={() => stopTemplate(null)}
                    data-testid="template-stop-keep">
              Stop, keep posted entries
            </Button>
            <Button size="sm" variant="outline"
                    className="border-red-500/50 text-red-300 hover:bg-red-500/10"
                    disabled={busy} onClick={() => stopTemplate(today)}
                    data-testid="template-stop-void-future">
              Stop and void entries from today
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringTab;
